import React from "react"

import { LeadViewInternalNotes } from "../../LeadViewPage/components/LeadViewInternalNotes.tsx"
import { type LeadWorkspaceTabContext } from "../lead-workspace-tabs.tsx"
import { LeadOverviewActivityCard } from "./LeadOverviewActivityCard.tsx"
import { LeadOverviewCustomerCard } from "./LeadOverviewCustomerCard.tsx"
import { LeadOverviewFilesCard } from "./LeadOverviewFilesCard.tsx"
import { LeadOverviewFinancialCard } from "./LeadOverviewFinancialCard.tsx"
import { LeadOverviewLocationCard } from "./LeadOverviewLocationCard.tsx"
import { LeadOverviewNeedsInfoSection } from "./LeadOverviewNeedsInfoSection.tsx"
import { LeadOverviewProjectCard } from "./LeadOverviewProjectCard.tsx"

/**
 * A dense CRM-dashboard grid matching the client's mockup row for row: two
 * equal-height cards up top, a ~44/56 split, then a 40/20/40 three-column
 * row (Files moved out of its own full-width row into this one, 2026-08-30,
 * once the client's mockup showed it as a compact peer of Activity/Notes
 * rather than a destination in its own right). Lifecycle
 * & Follow-Up (stage, operational state, owner, follow-up, appointment)
 * moved out entirely once the persistent header became their single UI
 * entry point (2026-08-30) — see `LeadWorkspaceHeader.tsx`; the deep-linked
 * "missing field" mark that used to land on that card now lands on the
 * header's own appointment control instead (`LEAD_HEADER_APPOINTMENT_TARGET`
 * in `missing/lead-missing-fields.ts`), the same way the owner control
 * already worked. Proposals & Contracts moved out to its own tab (it had no
 * slot in the mockup and reads as a workspace of its own) — see
 * `documents/LeadDocumentsTab.tsx`. Every card still owns only the fields it
 * writes — this only changes where they sit on the page.
 */
function LeadOverviewTab({
  lead,
  canEdit,
  saving,
  filterOptions,
  onPatch,
  onTabChange
}: LeadWorkspaceTabContext) {
  return (
    <div className={"flex flex-col gap-4"} data-testid={"lead-overview"}>
      <LeadOverviewNeedsInfoSection lead={lead} canEdit={canEdit} />

      {/* items-start, not items-stretch: the Customer summary is
          deliberately compact (~250-280px) and must not be stretched to
          Project's height, per the 2026-08-30 Customer card pass. */}
      <div className={"grid grid-cols-1 lg:grid-cols-2 gap-4 items-start"}>
        <LeadOverviewCustomerCard
          lead={lead}
          filterOptions={filterOptions}
          canEdit={canEdit}
          saving={saving}
          onPatch={onPatch}
        />
        <LeadOverviewProjectCard
          lead={lead}
          filterOptions={filterOptions}
          canEdit={canEdit}
          onPatch={onPatch}
        />
      </div>

      <div className={"grid grid-cols-1 lg:grid-cols-9 gap-4 items-stretch"}>
        <div className={"lg:col-span-4 min-w-0"}>
          <LeadOverviewFinancialCard lead={lead} />
        </div>
        <div className={"lg:col-span-5 min-w-0"}>
          <LeadOverviewLocationCard
            lead={lead}
            filterOptions={filterOptions}
            canEdit={canEdit}
            onPatch={onPatch}
          />
        </div>
      </div>

      {/* 40/20/40: Activity stays the primary column, Notes stays a narrow
          compact strip, and Files (2026-08-30) is a quick-glance summary
          here — full search/filter/infinite-scroll stays on the Files tab. */}
      <div className={"grid grid-cols-1 lg:grid-cols-10 gap-4 items-stretch"}>
        <div className={"lg:col-span-4 min-w-0"}>
          <LeadOverviewActivityCard
            lead={lead}
            onViewAll={() => onTabChange("activity")}
          />
        </div>
        <div className={"lg:col-span-2 min-w-0"}>
          <LeadViewInternalNotes
            notes={lead?.internal_notes}
            canEdit={canEdit}
            className={"h-full"}
            onPatch={onPatch}
          />
        </div>
        <div className={"lg:col-span-4 min-w-0"}>
          <LeadOverviewFilesCard
            attachments={lead?.attachments}
            canEdit={canEdit}
            saving={saving}
            onPatch={onPatch}
            onViewAll={() => onTabChange("files")}
          />
        </div>
      </div>
    </div>
  )
}

export { LeadOverviewTab }
